/**
 * Form validation utilities for widget pre-chat forms
 * Replaces Vue Formulate's built-in validation
 */

export const validators = {
  required: value => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') return value.trim() !== '';
    if (typeof value === 'boolean') return true;
    if (value instanceof Date) return !isNaN(value.getTime());
    return !!value;
  },

  email: value => {
    if (!value) return true; // Optional - passes if empty
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  },

  startsWithPlus: value => {
    if (!value) return true;
    return value.startsWith('+');
  },

  isValidPhoneNumber: value => {
    if (!value) return true;
    // International phone format: + followed by 7-15 digits
    return /^\+[1-9]\d{6,14}$/.test(value);
  },

  url: value => {
    if (!value) return true;
    try {
      const url = new URL(value);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
      return false;
    }
  },

  date: value => {
    if (!value) return true;
    if (value instanceof Date) return !isNaN(value.getTime());
    // Check string date format yyyy-MM-dd
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(value)) return false;
    const parsed = new Date(value);
    return !isNaN(parsed.getTime());
  },
};

/**
 * Validate a single field value against rules
 * @param {any} value - The field value to validate
 * @param {string} rules - Validation rules string (e.g., "bail|required|email")
 * @param {object} messages - Custom validation messages keyed by rule name
 * @returns {string|null} - Error message if validation fails, null if passes
 */
export function validateField(value, rules, messages = {}) {
  if (!rules) return null;

  // Parse rules, filtering out control rules
  const ruleList = rules
    .split('|')
    .filter(r => r && r !== 'bail' && r !== 'optional');

  // Check if field is optional (skip validation if empty and optional)
  const isOptional = rules.includes('optional');
  const isEmpty =
    value === null ||
    value === undefined ||
    (typeof value === 'string' && value.trim() === '');

  if (isOptional && isEmpty) {
    return null;
  }

  for (const rule of ruleList) {
    const validator = validators[rule];
    if (validator && !validator(value)) {
      return messages[rule] || `Validation failed: ${rule}`;
    }
  }

  return null;
}

/**
 * Validate all form fields
 * @param {object} formData - Object containing field values
 * @param {array} fields - Array of field definitions with name and validation rules
 * @param {object} messages - Custom validation messages
 * @returns {object} - Object with field names as keys and error messages as values
 */
export function validateAllFields(formData, fields, messages = {}) {
  const errors = {};

  fields.forEach(field => {
    const value = formData[field.name];
    const error = validateField(value, field.validation, messages);
    if (error) {
      errors[field.name] = error;
    }
  });

  return errors;
}

export default {
  validators,
  validateField,
  validateAllFields,
};
